import { ErrorModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	CircularProgress,
	InputAdornment,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";

import { SupplierChip } from "../components/SupplierChip";
import { Supplier } from "../models/Supplier";
import { getSuppliers } from "../repositories/SupplierRepository";

type UseSuppliersProps = {
	includeInactive?: boolean;
	refetchInterval?: number;
};

export const useSuppliers = ({
	includeInactive = false,
	refetchInterval,
}: UseSuppliersProps = {}) => {
	return useQuery<Supplier[], Error>({
		queryKey: ["suppliers", includeInactive],
		queryFn: ({ signal }) => getSuppliers({ includeInactive }, signal),
		refetchInterval,
	});
};

type Props = {
	label?: string;
	value: number | null;
	onChange?: (value: number | null, code?: string | null) => void;
	disabled?: boolean;
	required?: boolean;
	fieldError?: FieldError;
};

export const SupplierSelector = ({
	label = "Supplier",
	value,
	onChange = () => {},
	disabled = false,
	required = false,
	fieldError,
}: Props) => {
	const [error, setError] = useState("");

	const { data: suppliers, isLoading, error: e, isError } = useSuppliers();

	const selectedValue = suppliers?.find((s) => s.SupplierId === value) || null;

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Autocomplete
				size="small"
				clearOnEscape
				autoHighlight
				options={suppliers || []}
				getOptionLabel={(option) => option.SupplierCode}
				renderOption={(props, option) => {
					const { key, ...optionProps } = props as typeof props & {
						key: string;
					};
					return (
						<Box
							component="li"
							key={key}
							{...optionProps}
							sx={{ display: "flex", gap: 1 }}
						>
							<SupplierChip value={option.SupplierCode} />
							{option.SupplierCode}
						</Box>
					);
				}}
				disabled={disabled}
				value={selectedValue}
				onChange={(_event, newValue) => {
					onChange(
						newValue?.SupplierId ?? null,
						newValue?.SupplierCode ?? null,
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						margin="none"
						sx={{ backgroundColor: "white" }}
						inputProps={{
							...params.inputProps,
							"data-test-id": "supplier-selector",
						}}
						InputProps={{
							...params.InputProps,
							startAdornment: selectedValue ? (
								<InputAdornment position="start" sx={{ ml: 0.5 }}>
									<SupplierChip value={selectedValue.SupplierCode} />
								</InputAdornment>
							) : (
								params.InputProps.startAdornment
							),
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading suppliers"
			/>
		</>
	);
};
